import type { Inventory, Product } from '#root/api/clients/product/data-contracts'
import type { GiftOption } from '#types/cart'
import type { InteractionOrigin, ProductContext, ShippingMethodContext } from '#types/gtm'

export default function useQuickshop() {
  const api = useApi()
  const cart = useCartStore()
  const auth = useAuthStore()
  const route = useRoute()
  const { $gtm } = useNuxtApp()

  const updateSavedItemInCart = async (
    itemId: string, // id of save for later item
    selectedVariantId: string,
    url?: string
  ) => {
    try {
      cart.pending = true
      const savedItem = cart.savedItems.items.find((i) => i.itemId === itemId)

      await api.cart.$updateSavedForLater({
        productId: selectedVariantId,
        recipeId: '',
        pdpUrl: url || savedItem?.pageUrl,
        // @ts-expect-error incorrect contract type
        available: savedItem?.available || 'available33333333', // available33333333 is a known workaround - currently API is not using it, but it's required
        qty: savedItem?.qty,
        type: 'product',
        saveForLater: {
          // @ts-expect-error incorrect contract type
          consumerID: auth.consumerId,
          itemId,
          saveForLaterId: cart.savedItems.saveForLaterId
        }
      })

      await cart.fetchSavedCartItems()
      cart.errorMessage = ''
    }
    finally {
      cart.pending = false
    }
  }

  const updateItemInCart = async (
    itemId: string, // id of cart item
    currentProductId,
    newProductId: string,
    selectedVariantId: string,
    upc?: string,
    giftOption?: GiftOption,
    customAmount?: { amount?: number },
    storeId?: string
  ) => {
    try {
      cart.pending = true
      const productInCart = cart.items.find((i) => i.masterId === newProductId)

      if (currentProductId !== newProductId && !productInCart && !giftOption) {
        const currentProductInCart = cart.items.find((i) => i.masterId === currentProductId)
        if (currentProductInCart)
          await cart.remove(currentProductInCart.id)

        // Add New Product to cart
        await cart.add({
          productId: selectedVariantId,
          upc,
        }, {
          suppressMiniCart: true,
          suppressNotification: true,
          storeId
        })
      }
      else {
        const response: any = await api.cart.$updateItem(cart.basketId, {
          cartId: cart.basketId,
          itemId,
          productId: selectedVariantId,
          qty: productInCart ? productInCart.qty : giftOption ? 1 : 0,
          maxQty: productInCart ? productInCart.maxQty : giftOption ? 1 : 0,
          upc,
          ...getDigitalGiftOption(giftOption)
        }, {
          ...(customAmount?.amount ? customAmount : {})
        })

        cart.updateCartData(response)
        cart.clearCollectionPointFromSessionStorage()
        cart.getPaymentMethod()
        cart.errorMessage = ''
      }
      await cart.getApplicableShippingMethods()
    }
    finally {
      cart.pending = false
    }
  }

  const setProductDetailViewTracking = (
    activeProduct: MaybeRefOrGetter<string>,
    details: Ref<Product | null | undefined>,
    detailsPending: Ref<boolean>,
    inventory: Ref<Inventory | null>,
    inventoryPending: Ref<boolean>,
    productError: Ref<Error | null | undefined>,
    gtmConfig: (ProductContext & ShippingMethodContext & { appSource?: InteractionOrigin }) | undefined
  ) => {
    const trackedIds = new Set<string>()

    watch([activeProduct, () => details.value?.id, detailsPending, inventoryPending, productError], () => {
      const detailsId = details.value?.id

      if (
        detailsPending.value
        || inventoryPending.value
        || productError.value
        || !detailsId
        || toValue(activeProduct) !== detailsId
        || trackedIds.has(detailsId)
      ) {
        return
      }

      trackedIds.add(detailsId)

      $gtm.push('product.onProductDetailView', details.value, {
        viewType: 'Quick Shop',
        breadcrumbs: deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
        bundleId: gtmConfig?.bundleId,
        category: interactionOriginMap[gtmConfig?.appSource!] || gtmConfig?.category || history.state.category,
        ...(gtmConfig?.category && gtmConfig.list && { preserveListAndCategory: true }),
        searchTerm: route.query.q?.toString(),
        stockStatus: inventory.value?.variants[details.value?.masterId!]?.stockStatus,
        list: gtmConfig?.list
      })
    },)
  }

  return {
    updateSavedItemInCart,
    updateItemInCart,
    setProductDetailViewTracking
  }
}
